import React from 'react';

import { Box, Col, Row } from '@opendoor/atoms/box';
import { Text } from '@opendoor/atoms/text';
import { useAuth } from '@opendoor/auth-fe/context';
import { getInitials } from 'components/landing-pages-v2/utils/getInitials';

import type { MegaNavItem, MenuLink } from '../../types';
import { MenuLinkItem } from '../components/MenuLinkItem';
import { MobileMegaNavButton } from './MobileMegaNavButton';
import { MobileMenuSectionHeader } from './MobileMenuSectionHeader';

interface MobileMenuListProps {
  megaNavData?: MegaNavItem[];
  menuPanelNavLinks: MenuLink[];
  mainNavLinks: MenuLink[];
  slug: string;
  onSubMenuOpen: (itemName: string) => void;
  // The signed-in account items. `null`/`undefined` (logged out) hides the section.
  accountItems?: React.ReactNode;
  footer?: React.ReactNode;
}

export const MobileMenuList: React.FC<MobileMenuListProps> = ({
  megaNavData,
  menuPanelNavLinks,
  mainNavLinks,
  slug,
  onSubMenuOpen,
  accountItems,
  footer,
}) => {
  const { user: human } = useAuth();

  const fullName = human?.fullName;
  const initials = getInitials(fullName, human?.firstName);

  const renderAvatar = () => {
    if (!fullName) return null;
    return (
      <Row className="items-center gap-4 bg-od-bg-secondary px-6 py-4">
        <Box className="inline-flex h-11 w-11 items-center justify-center overflow-hidden rounded-xl bg-od-bg-inverse-primary shadow-[0_0_0_1px_rgb(88_64_50_/_0.16),inset_0_0_0_1px_rgb(255_255_255_/_0.3),inset_0_-4px_0_0_rgb(88_64_50_/_0.1)]">
          <Text as="span" className="text-label-sm font-medium text-od-content-inverse-primary">
            {initials.join('')}
          </Text>
        </Box>
        <Text as="span" className="text-label-md">
          {fullName}
        </Text>
      </Row>
    );
  };

  const hasPanelLinks = menuPanelNavLinks && menuPanelNavLinks.length > 0;

  const navItems = megaNavData
    ? megaNavData.map((item, idx) => {
        const hasSections = item.sections && item.sections.length > 0;
        const isLastNav = idx === megaNavData.length - 1 && !hasPanelLinks;

        if (hasSections) {
          return (
            <MobileMegaNavButton
              key={`mega-${idx}`}
              itemName={item.name}
              onClick={() => onSubMenuOpen(item.name)}
              isLast={isLastNav}
            />
          );
        }

        return (
          <MenuLinkItem
            key={`mega-${idx}`}
            href={item.href}
            name={item.name}
            analyticsName={`cosmos-mega-nav-mobile-${item.name.toLowerCase()}`}
            isActive={slug === item.href}
            isLast={isLastNav}
          />
        );
      })
    : mainNavLinks.map((nav, idx) => (
        <MenuLinkItem
          key={`main-${idx}`}
          href={nav.href}
          name={nav.name}
          analyticsName={`cosmos-landing-page-v2-nav-link-${nav.name}`}
          isActive={slug === nav.href}
          isLast={idx === mainNavLinks.length - 1 && !hasPanelLinks}
        />
      ));

  const content = (
    <>
      {renderAvatar()}
      {accountItems && <Col className="border-t border-od-border-primary">{accountItems}</Col>}
      <MobileMenuSectionHeader label="Resources" />
      <Col className="border-b border-od-border-primary">
        {navItems}
        {menuPanelNavLinks?.map((link, idx) => (
          <MenuLinkItem
            key={`panel-${idx}`}
            href={link.href}
            name={link.name}
            analyticsName={`cosmos-landing-page-v2-menu-link-${link.name}`}
            isActive={slug === link.href}
            isLast={idx === menuPanelNavLinks.length - 1}
          />
        ))}
      </Col>
    </>
  );

  return (
    <Col className="overflow-x-hidden bg-od-bg-primary">
      {content}
      {footer}
    </Col>
  );
};
